import { DEFAULT_GC_TIME_MS, DEFAULT_STALE_TIME_MS } from '@klareospace/pharos-lib-front-utils';
import type { TelemetryClient } from '@klareospace/pharos-lib-front-utils';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

/**
 * Create a TanStack QueryClient pre-wired with telemetry error reporting.
 *
 * Applies the shared default options (staleTime, gcTime, retry=false) and
 * attaches QueryCache / MutationCache `onError` handlers that forward all
 * query and mutation errors to the telemetry client as tracked exceptions.
 *
 * The handlers are fail-silent: errors thrown by the telemetry client are
 * swallowed by the client itself — this factory does not add extra try/catch.
 *
 * @param client - The telemetry client to forward errors to.
 * @returns A configured {@link QueryClient} instance.
 */
export function createTelemetryQueryClient(client: TelemetryClient): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: DEFAULT_GC_TIME_MS,
        retry: false,
        staleTime: DEFAULT_STALE_TIME_MS,
      },
    },
    mutationCache: new MutationCache({
      onError: (error: Error): void => {
        client.trackException(error);
      },
    }),
    queryCache: new QueryCache({
      onError: (error: Error): void => {
        client.trackException(error);
      },
    }),
  });
}
